/* Main-thread backend for the runtime-cache: the REAL source of truth for which
 * PHP versions are downloaded, how much space they use, and the download-with-
 * progress that warms the cache before a run.
 *
 * Everything maps onto the Cache Storage API (WASM_CACHE), the same cache the
 * Service Worker serves runtime assets from — so a version prefetched here is
 * instantly available to the worker's import()/fetch (cache-first), works fully
 * offline afterwards, and shows real cached bytes in Settings. All operations
 * degrade gracefully where Cache Storage is unavailable (e.g. SSR, old browsers,
 * or a private-mode quota wall) by reporting "nothing cached". */

import { WASM_CACHE } from './cache-names';
import { wasmUrl, runtimeUrl } from './version-registry';

const cacheApi = (): CacheStorage | null =>
  typeof caches !== 'undefined' ? caches : null;

export async function isCached(version: string): Promise<boolean> {
  const c = cacheApi();
  if (!c) return false;
  try {
    const cache = await c.open(WASM_CACHE);
    return (await cache.match(wasmUrl(version))) != null;
  } catch {
    return false;
  }
}

export async function cachedVersions(versions: string[]): Promise<string[]> {
  const flags = await Promise.all(versions.map(isCached));
  return versions.filter((_, i) => flags[i]);
}

/** Real byte size of a cached version's .wasm (0 if not cached / unknown).
 * Trusting content-length is safe here: every cache write goes through
 * downloadRuntime → fetchInto, which re-puts the entry with content-length set to
 * the decoded blob size (overwriting any SW-stored entry whose header would be the
 * brotli-compressed length). So the header always reflects real decompressed bytes. */
export async function cachedBytes(version: string): Promise<number> {
  const c = cacheApi();
  if (!c) return 0;
  try {
    const cache = await c.open(WASM_CACHE);
    const res = await cache.match(wasmUrl(version));
    if (!res) return 0;
    const len = res.headers.get('content-length');
    if (len) return parseInt(len, 10) || 0;
    // No content-length → read the body to measure exactly.
    return (await res.clone().blob()).size;
  } catch {
    return 0;
  }
}

export async function totalCachedBytes(versions: string[]): Promise<number> {
  const sizes = await Promise.all(versions.map(cachedBytes));
  return sizes.reduce((a, b) => a + b, 0);
}

/** Browser-reported {usage,quota} — for the quota gauge. */
export async function storageEstimate(): Promise<StorageEstimate | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
  try {
    return await navigator.storage.estimate();
  } catch {
    return null;
  }
}

export async function requestPersistentStorage(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted?.()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function removeCached(version: string): Promise<void> {
  const c = cacheApi();
  if (!c) return;
  try {
    const cache = await c.open(WASM_CACHE);
    await Promise.all([cache.delete(wasmUrl(version)), cache.delete(runtimeUrl(version))]);
  } catch {
    // cache unavailable
  }
}

export async function clearAllCached(): Promise<void> {
  const c = cacheApi();
  if (!c) return;
  try {
    await c.delete(WASM_CACHE);
  } catch {
    // cache unavailable
  }
}

async function fetchInto(cache: Cache, url: string, onFraction: (f: number) => void): Promise<void> {
  const res = await fetch(url);
  if (!res.ok || !res.body) throw new Error(`download failed: ${url} (${res.status})`);

  const total = parseInt(res.headers.get('content-length') ?? '0', 10);
  // Tee so we can both measure progress AND store the exact bytes downloaded.
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    if (total > 0) onFraction(Math.min(1, received / total));
  }
  if (total <= 0) onFraction(1); // unknown length → jump to done on completion

  // Reconstruct a Response carrying a real content-length so cachedBytes() is exact.
  const body = new Blob(chunks as BlobPart[]);
  const headers = new Headers(res.headers);
  headers.set('content-length', String(body.size));
  await cache.put(url, new Response(body, { status: 200, headers }));
}

/** Progress phases surfaced to the download modal per version. */
export type DownloadPhase = 'wait' | 'download' | 'init' | 'ready';
export interface DownloadProgress {
  phase: DownloadPhase;
  /** download completion 0..100. */
  pct: number;
}

/**
 * Download a version's runtime into the cache with progress.
 * Loader (small) then wasm (large, ~99% of the bytes) → pct tracks the wasm.
 */
export async function downloadRuntime(
  version: string,
  onProgress: (p: DownloadProgress) => void,
): Promise<void> {
  const c = cacheApi();
  if (!c) {
    // No Cache Storage → nothing to warm; the worker will fetch from network.
    onProgress({ phase: 'ready', pct: 100 });
    return;
  }
  if (await isCached(version)) {
    onProgress({ phase: 'ready', pct: 100 });
    return;
  }

  const cache = await c.open(WASM_CACHE);
  onProgress({ phase: 'download', pct: 0 });
  // Loader is tiny; fetch it first (no visible progress weight).
  await fetchInto(cache, runtimeUrl(version), () => {});
  await fetchInto(cache, wasmUrl(version), (f) =>
    onProgress({ phase: 'download', pct: Math.round(f * 100) }),
  );
  // "init" = the worker still has to instantiate the module on first run.
  onProgress({ phase: 'ready', pct: 100 });
}
